import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Container,
  Button,
  Alert,
  Grid,
  Card,
  Fade,
  LinearProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  FormHelperText,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  ColorLens as ColorLensIcon,
  Preview as PreviewIcon,
  RestartAlt as ResetIcon,
  Palette as PaletteIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  Style as StyleIcon,
  TextFields as TextFieldsIcon,
  Image as ImageIcon,
  Animation as AnimationIcon,
  Security as SecurityIcon,
  Accessibility as AccessibilityIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const SettingsSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: 16,
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '1px solid',
  borderColor: alpha(theme.palette.primary.main, 0.1),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  }
}));

const Settings = ({ formSettings, onUpdateSettings }) => {
  const { t, i18n } = useTranslation();
  const [expandedSections, setExpandedSections] = useState({
    login: true,
    appearance: true,
    document: false,
    animation: false,
    accessibility: false,
    security: false,
  });

  const [localSettings, setLocalSettings] = useState({
    backgroundColor: '#ffffff',
    titleColor: '#000000',
    descriptionColor: '#666666',
    submitButtonColor: '#1976d2',
    submitButtonTextColor: '#ffffff',
    fieldSize: 'medium',
    formWidth: 'medium',
    borderRadius: 8,
    spacing: 'medium',
    fontFamily: 'Roboto',
    fontSize: 'medium',
    documentPrefix: 'DOK',
    documentCode: '001',
    showLogo: true,
    logoPosition: 'center',
    enableShadow: true,
    shadowIntensity: 'medium',
    animationEnabled: true,
    transitionSpeed: 'medium',
    hoverEffects: true,
    fadeInAnimation: true,
    slideAnimation: false,
    accessibilityEnabled: false,
    highContrastMode: false,
    largeText: false,
    keyboardNavigation: true,
    screenReaderSupport: true,
    securityEnabled: false,
    captchaEnabled: false,
    encryptData: false,
    requireAuth: false,
    maxSubmissions: 100,
    sessionTimeout: 30,
    ipWhitelist: [],
    formExpiry: 'never',
    loginRequired: true,
    ...formSettings
  });

  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (formSettings) {
      setLocalSettings(prev => ({
        ...prev,
        ...formSettings
      }));
    }
  }, [formSettings]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (onUpdateSettings) {
        await onUpdateSettings(localSettings);
      }
      setHasChanges(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Settings save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setLocalSettings({
      backgroundColor: '#ffffff',
      titleColor: '#000000',
      descriptionColor: '#666666',
      submitButtonColor: '#1976d2',
      submitButtonTextColor: '#ffffff',
      fieldSize: 'medium',
      formWidth: 'medium',
      borderRadius: 8,
      spacing: 'medium',
      fontFamily: 'Roboto',
      fontSize: 'medium',
      documentPrefix: 'DOK',
      documentCode: '001',
      showLogo: true,
      logoPosition: 'center',
      enableShadow: true,
      shadowIntensity: 'medium',
      animationEnabled: true,
      transitionSpeed: 'medium',
      hoverEffects: true,
      fadeInAnimation: true,
      slideAnimation: false,
      accessibilityEnabled: false,
      highContrastMode: false,
      largeText: false,
      keyboardNavigation: true,
      screenReaderSupport: true,
      securityEnabled: false,
      captchaEnabled: false,
      encryptData: false,
      requireAuth: false,
      maxSubmissions: 100,
      sessionTimeout: 30,
      ipWhitelist: [],
      formExpiry: 'never',
      loginRequired: true,
    });
    setHasChanges(true);
  };

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      maxHeight: 'calc(100vh - 140px)',
      bgcolor: 'grey.50',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Üst bildirimler */}
      {isSaving && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }} />}

      <Fade in={showSuccess}>
        <Alert
          severity="success"
          sx={{
            position: 'absolute',
            top: isSaving ? 4 : 0,
            left: 8,
            right: 8,
            zIndex: 9,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {t('settings_saved_successfully')}
        </Alert>
      </Fade>

      <Fade in={hasChanges && !isSaving}>
        <Alert
          severity="info"
          sx={{
            position: 'absolute',
            top: (isSaving ? 4 : 0) + (showSuccess ? 60 : 0),
            left: 8,
            right: 8,
            zIndex: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {t('unsaved_changes_warning')}
        </Alert>
      </Fade>

      {/* İçerik alanı - Scroll edilebilir accordion tasarım */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        px: 2,
        py: 2,
        bgcolor: 'grey.50',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            background: '#a8a8a8',
          },
        },
      }}>
        <Container maxWidth="lg" sx={{ py: 0 }}>
          <Stack spacing={2}>
            {/* Görünüm Ayarları */}
            <SettingsSection style={{ marginTop: 16 }}>


              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 0,
                pb: 2,
                pt: 2,
                borderColor: 'error.main',
                background: 'linear-gradient(90deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
                mx: -3,
                px: 3,
                borderRadius: '16px 16px 0 0',
                cursor: 'pointer'
              }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                }}>
                  {localSettings.loginRequired ? <Visibility sx={{ color: 'white', fontSize: 24 }} /> : <VisibilityOff sx={{ color: 'white', fontSize: 24 }} />}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("loginRequiredControl")}
                  </Typography>
                </Box>
                <Switch
                  checked={localSettings.loginRequired}
                  onChange={(e) => handleChange('loginRequired', e.target.checked)}
                />
              </Box>
            </SettingsSection>
            <SettingsSection>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: expandedSections.appearance ? 3 : 0,
                pb: 2,
                borderBottom: expandedSections.appearance ? '2px solid' : 'none',
                borderColor: 'primary.main',
                background: 'linear-gradient(90deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
                mx: -3,
                px: 3,
                borderRadius: '16px 16px 0 0',
                cursor: 'pointer'
              }}
                onClick={() => toggleSection('appearance')}
              >
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                }}>
                  <StyleIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                    {t('appearance')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Formunuzun görünümünü ve renklerini özelleştirin
                  </Typography>
                </Box>
                <IconButton
                  size="large"
                  sx={{
                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' }
                  }}
                >
                  {expandedSections.appearance ?
                    <ExpandLessIcon sx={{ color: 'primary.main' }} /> :
                    <ExpandMoreIcon sx={{ color: 'primary.main' }} />
                  }
                </IconButton>
              </Box>

              <Collapse in={expandedSections.appearance}>
                <Grid container spacing={3}>
                  {/* Renk ayarları */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#e3f2fd', border: '1px solid #bbdefb' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                        🎨 {t('colors')}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="color"
                            label={t('background_color')}
                            value={localSettings.backgroundColor}
                            onChange={(e) => handleChange('backgroundColor', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="color"
                            label={t('title_color')}
                            value={localSettings.titleColor}
                            onChange={(e) => handleChange('titleColor', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="color"
                            label={t('submit_button_color')}
                            value={localSettings.submitButtonColor}
                            onChange={(e) => handleChange('submitButtonColor', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="color"
                            label={t('button_text_color')}
                            value={localSettings.submitButtonTextColor}
                            onChange={(e) => handleChange('submitButtonTextColor', e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Boyut ayarları */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
                        📏 {t('sizes')}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('form_width')}</InputLabel>
                            <Select
                              value={localSettings.formWidth}
                              onChange={(e) => handleChange('formWidth', e.target.value)}
                            >
                              <MenuItem value="small">{t('small')}</MenuItem>
                              <MenuItem value="medium">{t('medium')}</MenuItem>
                              <MenuItem value="large">{t('large')}</MenuItem>
                              <MenuItem value="full">{t('full_width')}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('field_size')}</InputLabel>
                            <Select
                              value={localSettings.fieldSize}
                              onChange={(e) => handleChange('fieldSize', e.target.value)}
                            >
                              <MenuItem value="small">{t('small')}</MenuItem>
                              <MenuItem value="medium">{t('medium')}</MenuItem>
                              <MenuItem value="large">{t('large')}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Collapse>
            </SettingsSection>

            {/* Doküman Ayarları */}
            <SettingsSection>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: expandedSections.document ? 3 : 0,
                pb: 2,
                borderBottom: expandedSections.document ? '2px solid' : 'none',
                borderColor: 'secondary.main',
                background: 'linear-gradient(90deg, rgba(156, 39, 176, 0.05) 0%, rgba(156, 39, 176, 0.02) 100%)',
                mx: -3,
                px: 3,
                borderRadius: '16px 16px 0 0',
                cursor: 'pointer'
              }}
                onClick={() => toggleSection('document')}
              >
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                }}>
                  <DescriptionIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main', mb: 0.5 }}>
                    {t('document')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Form doküman ve metin ayarları
                  </Typography>
                </Box>
                <IconButton
                  size="large"
                  sx={{
                    bgcolor: 'rgba(156, 39, 176, 0.1)',
                    '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.2)' }
                  }}
                >
                  {expandedSections.document ?
                    <ExpandLessIcon sx={{ color: 'secondary.main' }} /> :
                    <ExpandMoreIcon sx={{ color: 'secondary.main' }} />
                  }
                </IconButton>
              </Box>

              <Collapse in={expandedSections.document}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#e8f5e8', border: '1px solid #c8e6c9' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
                        📄 Doküman Numarası
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Doküman Öneki"
                            value={localSettings.documentPrefix || 'DOK'}
                            onChange={(e) => handleChange('documentPrefix', e.target.value)}
                            placeholder="DOK"
                            helperText="Doküman türü öneki"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: 'success.main',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'success.dark',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'success.main',
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Doküman Kodu"
                            value={localSettings.documentCode || '001'}
                            onChange={(e) => handleChange('documentCode', e.target.value)}
                            placeholder="001"
                            helperText="Sıra numarası"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: 'success.main',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'success.dark',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'success.main',
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                            Önizleme: {localSettings.documentPrefix || 'DOK'}-{localSettings.documentCode || '001'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#f3e5f5', border: '1px solid #e1bee7' }}>
                      <FormControl fullWidth>
                        <InputLabel>📝 {t('font_family')}</InputLabel>
                        <Select
                          value={localSettings.fontFamily}
                          onChange={(e) => handleChange('fontFamily', e.target.value)}
                        >
                          <MenuItem value="Roboto">Roboto</MenuItem>
                          <MenuItem value="Arial">Arial</MenuItem>
                          <MenuItem value="Helvetica">Helvetica</MenuItem>
                          <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                        </Select>
                      </FormControl>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                      <FormControl fullWidth>
                        <InputLabel>📏 {t('font_size')}</InputLabel>
                        <Select
                          value={localSettings.fontSize}
                          onChange={(e) => handleChange('fontSize', e.target.value)}
                        >
                          <MenuItem value="small">{t('small')}</MenuItem>
                          <MenuItem value="medium">{t('medium')}</MenuItem>
                          <MenuItem value="large">{t('large')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Paper>
                  </Grid>
                </Grid>
              </Collapse>
            </SettingsSection>

            {/* Animasyon Ayarları */}
            <SettingsSection>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: expandedSections.animation ? 3 : 0,
                pb: 2,
                borderBottom: expandedSections.animation ? '2px solid' : 'none',
                borderColor: 'success.main',
                background: 'linear-gradient(90deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)',
                mx: -3,
                px: 3,
                borderRadius: '16px 16px 0 0',
                cursor: 'pointer'
              }}
                onClick={() => toggleSection('animation')}
              >
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                }}>
                  <AnimationIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main', mb: 0.5 }}>
                    {t('animation')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Form geçiş ve animasyon efektleri
                  </Typography>
                </Box>
                <IconButton
                  size="large"
                  sx={{
                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                    '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.2)' }
                  }}
                >
                  {expandedSections.animation ?
                    <ExpandLessIcon sx={{ color: 'success.main' }} /> :
                    <ExpandMoreIcon sx={{ color: 'success.main' }} />
                  }
                </IconButton>
              </Box>

              <Collapse in={expandedSections.animation}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#e8f5e8', border: '1px solid #c8e6c9' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={localSettings.animationEnabled}
                            onChange={(e) => handleChange('animationEnabled', e.target.checked)}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              ✨ {t('enable_animations')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('animation_help')}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={localSettings.hoverEffects}
                            onChange={(e) => handleChange('hoverEffects', e.target.checked)}
                            color="warning"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              🎯 {t('hover_effects')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('hover_effects_help')}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Collapse>
            </SettingsSection>

            {/* Erişilebilirlik Ayarları */}
            <SettingsSection>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: expandedSections.accessibility ? 3 : 0,
                pb: 2,
                borderBottom: expandedSections.accessibility ? '2px solid' : 'none',
                borderColor: 'info.main',
                background: 'linear-gradient(90deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)',
                mx: -3,
                px: 3,
                borderRadius: '16px 16px 0 0',
                cursor: 'pointer'
              }}
                onClick={() => toggleSection('accessibility')}
              >
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                }}>
                  <AccessibilityIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main', mb: 0.5 }}>
                    {t('accessibility')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Formunuzu herkes için erişilebilir hale getirin
                  </Typography>
                </Box>
                <IconButton
                  size="large"
                  sx={{
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                    '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.2)' }
                  }}
                >
                  {expandedSections.accessibility ?
                    <ExpandLessIcon sx={{ color: 'info.main' }} /> :
                    <ExpandMoreIcon sx={{ color: 'info.main' }} />
                  }
                </IconButton>
              </Box>

              <Collapse in={expandedSections.accessibility}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#e3f2fd', border: '1px solid #bbdefb' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={localSettings.accessibilityEnabled}
                            onChange={(e) => handleChange('accessibilityEnabled', e.target.checked)}
                            color="info"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              ♿ {t('accessibility_features')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('enable_form_accessibility_features')}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Collapse>
            </SettingsSection>

            {/* Güvenlik Ayarları */}
            <SettingsSection>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: expandedSections.security ? 3 : 0,
                pb: 2,
                borderBottom: expandedSections.security ? '2px solid' : 'none',
                borderColor: 'error.main',
                background: 'linear-gradient(90deg, rgba(244, 67, 54, 0.05) 0%, rgba(244, 67, 54, 0.02) 100%)',
                mx: -3,
                px: 3,
                borderRadius: '16px 16px 0 0',
                cursor: 'pointer'
              }}
                onClick={() => toggleSection('security')}
              >
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                }}>
                  <SecurityIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main', mb: 0.5 }}>
                    {t('security')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Form güvenlik ayarlarını yönetin
                  </Typography>
                </Box>
                <IconButton
                  size="large"
                  sx={{
                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                    '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' }
                  }}
                >
                  {expandedSections.security ?
                    <ExpandLessIcon sx={{ color: 'error.main' }} /> :
                    <ExpandMoreIcon sx={{ color: 'error.main' }} />
                  }
                </IconButton>
              </Box>

              <Collapse in={expandedSections.security}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#ffebee', border: '1px solid #ffcdd2' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={localSettings.securityEnabled}
                            onChange={(e) => handleChange('securityEnabled', e.target.checked)}
                            color="error"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              🔐 {t('security_features')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('enable_form_security_features')}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={localSettings.captchaEnabled}
                            onChange={(e) => handleChange('captchaEnabled', e.target.checked)}
                            color="warning"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              🤖 {t('captcha_protection')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('captcha_protection_help')}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Collapse>
            </SettingsSection>
          </Stack>
        </Container>
      </Box>

      {/* Alt kontrol paneli - Sabit alt kısım */}
      <Paper
        elevation={8}
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(10px)',
          zIndex: 10
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {hasChanges ? t('unsaved_changes') : t('Değişiklik yok')}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ResetIcon />}
            onClick={handleReset}
            disabled={isSaving}
            sx={{
              textTransform: 'none',
              borderRadius: 2
            }}
          >
            {t('reset')}
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              minWidth: 120
            }}
          >
            {isSaving ? t('saving') : t('save_changes')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Settings;
